import {
  ExceptionFilter,
  Catch,
  ArgumentsHost,
  HttpException,
  HttpStatus,
  Logger,
} from '@nestjs/common';
import { FastifyRequest, FastifyReply } from 'fastify';
import { ObjectLiteral } from '../types/global';

// Define the ErrorType enum
export enum ErrorType {
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  AUTHORIZATION_ERROR = 'AUTHORIZATION_ERROR',
  NOT_FOUND_ERROR = 'NOT_FOUND_ERROR',
  CONFLICT_ERROR = 'CONFLICT_ERROR',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  BAD_REQUEST_ERROR = 'BAD_REQUEST_ERROR',
  FORBIDDEN_ERROR = 'FORBIDDEN_ERROR',
}

// Define the response type
export type ApiErrorResponse = {
  success: false;
  statusCode: number;
  message: string;
  errorType: ErrorType;
  errors?: string[];
};

@Catch(HttpException)
export class HttpExceptionFilter implements ExceptionFilter {
  private readonly logger = new Logger(HttpExceptionFilter.name);

  catch(exception: HttpException, host: ArgumentsHost) {
    const ctx = host.switchToHttp();
    const response = ctx.getResponse<FastifyReply>();
    const request = ctx.getRequest<FastifyRequest>();
    const status = exception.getStatus();
    const exceptionResponse = exception.getResponse();
    let message: string = 'An error occurred';
    let errors: string[] = [];

    if (typeof exceptionResponse === 'string') {
      message = exceptionResponse;
    } else if (
      typeof exceptionResponse === 'object' &&
      exceptionResponse !== null
    ) {
      const responseObj: ObjectLiteral = exceptionResponse;
      message =
        (responseObj.message as string) ||
        (responseObj.error as string) ||
        message;

      if (Array.isArray(responseObj.message)) {
        errors = responseObj.message as string[];
        message = 'Validation failed';
      } else if (responseObj.errors && Array.isArray(responseObj.errors)) {
        errors = responseObj.errors as string[];
      }
    }

    const errorType = this.mapStatusToErrorType(status);
    const errorResponse: ApiErrorResponse = {
      success: false,
      statusCode: status,
      message,
      errorType,
      ...(errors && { errors }),
    };

    this.logger.error(
      `HTTPException Error: ${message} (Status: ${status})`,
      exception.stack,
      `${request.method} ${request.url}`,
    );

    response.status(status).send(errorResponse);
  }

  private mapStatusToErrorType(status: HttpStatus): ErrorType {
    switch (status) {
      case HttpStatus.BAD_REQUEST:
        return ErrorType.BAD_REQUEST_ERROR;
      case HttpStatus.UNAUTHORIZED:
        return ErrorType.AUTHENTICATION_ERROR;
      case HttpStatus.FORBIDDEN:
        return ErrorType.AUTHORIZATION_ERROR;
      case HttpStatus.NOT_FOUND:
        return ErrorType.NOT_FOUND_ERROR;
      case HttpStatus.CONFLICT:
        return ErrorType.CONFLICT_ERROR;
      case HttpStatus.UNPROCESSABLE_ENTITY:
        return ErrorType.VALIDATION_ERROR;
      case HttpStatus.INTERNAL_SERVER_ERROR:
        return ErrorType.INTERNAL_SERVER_ERROR;
      default:
        return ErrorType.INTERNAL_SERVER_ERROR;
    }
  }
}
